// Add Lines picker for the Billing tab's invoices.
//
// Putting lines on an invoice is a weekly job done a section at a time: this
// week's labor, then this week's equipment, then whatever third-party cost has
// vouchered. The picker is shaped for that rather than for one flat list:
//
//   - Three sections — Labor | Equipment | Third Party — each showing what is
//     ready in it, so one click narrows to the section being worked.
//   - Cost type chips inside a section. For labor that is the point: only
//     511 ST, 512 OT and 515 per diem ever bill. The rest of the 51xxx range
//     (burden, taxes, insurance …) is labor cost, never labor billing — it is
//     not offered here, and the labor section says how much of it there is so
//     nobody goes looking for it.
//   - A signed tag on labor and equipment lines: is the work date under a
//     week summary the client has signed (billingTicketDocs.signedByDate)?
//     Cost history carries a work date but no crew shift, so the tie is by
//     date. Third-party lines ride vendor paper, not field tickets, and carry
//     no tag.
//   - Lines grouped under their week ending (Monday–Sunday, the field
//     ticket's week), with a checkbox per week — a signed week goes on the
//     invoice in one tick.
//
// Nothing here prices or saves: rows arrive priced from billing.js's txns and
// leave through onAdd (addToSummary). Selection lives on the line key, so it
// survives every filter change, and the add reads from the full line set —
// narrowing the view can never silently drop a ticked line.

import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { View, Text, Pressable, ScrollView, TextInput } from '../lib/react-native-compat';
import { Chip, OutlineButton } from './procurementFilters';
import { weekEnding } from './billingTicketDocs';

const th = { fontSize: 11, fontWeight: '700', color: '#475569', paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 };
const td = { fontSize: 12, color: '#1e293b', paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8 };

export const ADD_SECTIONS = ['Labor', 'Equipment', 'Third Party'];
export const sectionOfLine = (t) => (t.cls === 'Labor' || t.cls === 'Equipment' ? t.cls : 'Third Party');

// The labor cost types that bill, by the names the field uses for them.
const LABOR_CT_LABEL = { 51100: 'ST', 51200: 'OT', 51500: 'Per Diem' };
const ctLabel = (ct, desc) => {
    const short = String(ct).slice(0, 3);
    const name = LABOR_CT_LABEL[ct] || String(desc || '').trim();
    return name ? `${short} ${name}` : short;
};

// Rows rendered at once. Select-all and the week checkboxes still reach every
// match — the cap only limits the DOM.
const ROW_CAP = 300;

// Signature tag per summary stage. Monochrome-ish on purpose: Signed is the
// only one that should catch the eye.
const SIG_TAG = {
    Signed: { label: 'Signed', bg: '#dcfce7', fg: '#166534' },
    'Sent for Signature': { label: 'Out', bg: '#fef3c7', fg: '#92400e' },
    Declined: { label: 'Declined', bg: '#fee2e2', fg: '#991b1b' },
    Validated: { label: 'Not sent', bg: '#f1f5f9', fg: '#475569' },
    Draft: { label: 'Not sent', bg: '#f1f5f9', fg: '#475569' },
};
const SIG_FILTERS = ['All', 'Signed', 'Not signed'];

export const SigTag = ({ sig }) => {
    if (!sig) return <Text style={{ fontSize: 11, color: '#cbd5e1' }}>—</Text>;
    const s = SIG_TAG[sig.stage] || SIG_TAG.Draft;
    const tip = sig.docs.map((x) => `${x.name}: ${x.stage}${x.at ? ` ${moment.utc(x.at).local().format('M/D h:mm A')}` : ''}`).join('\n');
    return (
        <View style={{ backgroundColor: s.bg, borderRadius: 3, paddingTop: 1, paddingBottom: 1, paddingLeft: 5, paddingRight: 5, alignSelf: 'flex-start' }}>
            <Text title={tip} style={{ fontSize: 10, fontWeight: '700', color: s.fg, whiteSpace: 'nowrap' }}>{s.label}</Text>
        </View>
    );
};

export default function BillingAddLines({
    inv,                 // the invoice receiving lines
    lines = [],          // every line that can go on it (priced txns rows)
    otherLabor,          // { n, cost } — 51xxx cost that never bills, for the labor note
    signedBy = {},       // work date -> { stage, docs } (signedByDate)
    sigLoading = false,
    onAdd,               // (inv, list) => void
    onClose,
    money, fDate, rateText, CheckBox, DateInput,
}) {
    const [sel, setSel] = useState({});
    const [q, setQ] = useState('');
    const [from, setFrom] = useState('');
    const [until, setUntil] = useState('');
    const [section, setSection] = useState('');
    const [cts, setCts] = useState([]);
    const [sigFilter, setSigFilter] = useState('All');

    // Only labor and equipment ride field tickets.
    const sigOf = (t) => (t.cls === 'Labor' || t.cls === 'Equipment' ? signedBy[t.date] || null : null);
    const ticketed = (t) => t.cls === 'Labor' || t.cls === 'Equipment';

    // What is ready per section, before any narrowing — the section strip.
    const bySection = useMemo(() => {
        const m = {};
        ADD_SECTIONS.forEach((s) => { m[s] = { n: 0, bill: 0, signed: 0, signedBill: 0 }; });
        lines.forEach((t) => {
            const e = m[sectionOfLine(t)];
            e.n += 1;
            e.bill += t.bill;
            if (sigOf(t)?.stage === 'Signed') { e.signed += 1; e.signedBill += t.bill; }
        });
        return m;
    }, [lines, signedBy]);

    // Cost types present in the open section, for its chips.
    const ctOptions = useMemo(() => {
        if (!section) return [];
        const m = new Map();
        lines.forEach((t) => {
            if (sectionOfLine(t) !== section || !t.ct) return;
            const e = m.get(t.ct) || { ct: t.ct, desc: t.ctDesc, n: 0 };
            e.n += 1;
            m.set(t.ct, e);
        });
        return [...m.values()].sort((a, b) => String(a.ct).localeCompare(String(b.ct)));
    }, [lines, section]);

    const pickSection = (s) => { setSection(section === s ? '' : s); setCts([]); };

    const choices = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return lines.filter((t) => {
            if (needle && ![t.cc, t.ccDesc, t.who, t.eq, t.po, t.ds, t.vinvNo].join(' ').toLowerCase().includes(needle)) return false;
            if (section && sectionOfLine(t) !== section) return false;
            if (cts.length && !cts.includes(t.ct)) return false;
            if (from && t.date < from) return false;
            if (until && t.date > until) return false;
            if (sigFilter === 'Signed') return sigOf(t)?.stage === 'Signed';
            if (sigFilter === 'Not signed') return ticketed(t) && sigOf(t)?.stage !== 'Signed';
            return true;
        }).sort((a, b) => String(b.date).localeCompare(String(a.date)));
    }, [lines, q, section, cts, from, until, sigFilter, signedBy]);

    // Newest week first; lines with no work date fall in a bucket of their own.
    const weeks = useMemo(() => {
        const m = new Map();
        choices.forEach((t) => {
            const we = weekEnding(t.date) || '';
            let g = m.get(we);
            if (!g) { g = { we, rows: [], bill: 0, hrs: 0, days: new Set(), signedDays: new Set() }; m.set(we, g); }
            g.rows.push(t);
            g.bill += t.bill;
            g.hrs += t.hrs || 0;
            if (ticketed(t) && t.date) {
                g.days.add(t.date);
                if (sigOf(t)?.stage === 'Signed') g.signedDays.add(t.date);
            }
        });
        return [...m.values()].sort((a, b) => String(b.we).localeCompare(String(a.we)));
    }, [choices, signedBy]);

    const addList = lines.filter((t) => sel[t.key]);
    const allChecked = choices.length > 0 && choices.every((t) => sel[t.key]);
    // Ticks merge with the ones made under other filters rather than replacing them.
    const toggleMany = (rows, on) => setSel((p) => {
        const next = { ...p };
        rows.forEach((t) => { if (on) next[t.key] = true; else delete next[t.key]; });
        return next;
    });
    const toggleOne = (t) => setSel((p) => ({ ...p, [t.key]: !p[t.key] }));

    const markupText = (t) => (t.hourly ? rateText(t) || 'rate' : t.pt ? (t.mk ? `PT +${t.mk}%` : 'PT at cost') : `+${t.mk}%`);

    let budget = ROW_CAP;

    return (
        <Pressable
            onPress={onClose}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
                backgroundColor: 'rgba(15, 23, 42, 0.5)', alignItems: 'center', justifyContent: 'center',
                cursor: 'default', padding: 24,
            }}
        >
            <Pressable
                onPress={(e) => e.stopPropagation && e.stopPropagation()}
                style={{
                    backgroundColor: '#fff', borderRadius: 8, width: 1040, maxWidth: '96vw',
                    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)', cursor: 'default',
                }}
            >
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingTop: 16, paddingBottom: 12, paddingLeft: 22, paddingRight: 22, borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                    <View>
                        <Text style={{ fontSize: 16, fontWeight: '700', color: '#0f172a' }}>Add Lines to {inv.no}</Text>
                        <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            {choices.length === lines.length
                                ? `${lines.length} unbilled transaction${lines.length === 1 ? '' : 's'} available`
                                : `${choices.length} of ${lines.length} unbilled transactions match`} · added lines bill at today&apos;s markup rates
                        </Text>
                    </View>
                    <Pressable onPress={onClose}>
                        <Text style={{ fontSize: 18, color: '#64748b' }}>×</Text>
                    </Pressable>
                </View>

                <View style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 22, paddingRight: 22 }}>
                    {/* Section strip: what is ready in each, and the way in. */}
                    <View style={{ flexDirection: 'row', marginBottom: 10 }}>
                        {ADD_SECTIONS.map((s, i) => {
                            const e = bySection[s];
                            const on = section === s;
                            return (
                                <Pressable
                                    key={s}
                                    onPress={() => pickSection(s)}
                                    style={{
                                        flexGrow: 1, flexBasis: 0, marginLeft: i ? 10 : 0,
                                        paddingTop: 8, paddingBottom: 8, paddingLeft: 12, paddingRight: 12,
                                        borderRadius: 6, borderWidth: 1, borderStyle: 'solid',
                                        borderColor: on ? '#0f172a' : '#e2e8f0',
                                        backgroundColor: on ? '#f8fafc' : '#fff',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#0f172a' }}>{s}</Text>
                                        <Text style={{ fontSize: 13, fontWeight: '700', color: e.n ? '#166534' : '#cbd5e1' }}>{e.n ? money(e.bill) : '—'}</Text>
                                    </View>
                                    <Text style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                                        {`${e.n} line${e.n === 1 ? '' : 's'} ready`}
                                        {s !== 'Third Party' && e.n
                                            ? (sigLoading ? ' · checking signatures…' : ` · ${e.signed} under a signed summary (${money(e.signedBill)})`)
                                            : ''}
                                    </Text>
                                </Pressable>
                            );
                        })}
                    </View>

                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
                        <TextInput
                            value={q}
                            onChangeText={setQ}
                            placeholder="Search cost code, employee, vendor, PO, description..."
                            style={{
                                width: 300, fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1',
                                paddingTop: 6, paddingBottom: 6, paddingLeft: 8, paddingRight: 8,
                                marginRight: 10, marginBottom: 6,
                            }}
                        />
                        <DateInput value={from} onValueChange={setFrom} />
                        <DateInput value={until} onValueChange={setUntil} />
                        <Text style={{ fontSize: 10, fontWeight: '700', color: '#94a3b8', letterSpacing: 0.5, marginLeft: 6, marginRight: 8, marginBottom: 6 }}>WEEK SUMMARY</Text>
                        {SIG_FILTERS.map((f) => (
                            <Chip key={f} label={f} active={sigFilter === f} onPress={() => setSigFilter(f)} />
                        ))}
                    </View>

                    {section ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
                            <Text style={{ fontSize: 10, fontWeight: '700', color: '#94a3b8', letterSpacing: 0.5, marginRight: 8, marginBottom: 6 }}>COST TYPE</Text>
                            {ctOptions.map((o) => (
                                <Chip
                                    key={o.ct}
                                    label={`${ctLabel(o.ct, o.desc)} (${o.n})`}
                                    active={cts.includes(o.ct)}
                                    onPress={() => setCts(cts.includes(o.ct) ? cts.filter((c) => c !== o.ct) : [...cts, o.ct])}
                                />
                            ))}
                            {section === 'Labor' ? (
                                <Text style={{ fontSize: 11, color: '#64748b', marginLeft: 4, marginBottom: 6 }}>
                                    Only 511 ST, 512 OT and 515 per diem bill
                                    {otherLabor && otherLabor.n
                                        ? ` — ${otherLabor.n.toLocaleString('en-US')} other 51xxx line${otherLabor.n === 1 ? '' : 's'} (${money(otherLabor.cost)} of burden, taxes, insurance) are not billable and are not offered.`
                                        : '.'}
                                </Text>
                            ) : null}
                        </View>
                    ) : null}

                    {choices.length ? (
                        <>
                            <View style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                <View style={[th, { width: 34 }]}>
                                    <CheckBox checked={allChecked} onPress={() => toggleMany(choices, !allChecked)} />
                                </View>
                                <Text style={[th, { width: 72 }]}>DATE</Text>
                                <Text style={[th, { width: 92 }]}>COST CODE</Text>
                                <Text style={[th, { width: 96 }]}>TYPE</Text>
                                <Text style={[th, { width: 160 }]}>EMPLOYEE / VENDOR</Text>
                                <Text style={[th, { flexGrow: 1, flexBasis: 0 }]}>DESCRIPTION</Text>
                                <Text style={[th, { width: 56, textAlign: 'right' }]}>HOURS</Text>
                                <Text style={[th, { width: 90, textAlign: 'right' }]}>COST</Text>
                                <Text style={[th, { width: 84, textAlign: 'right' }]}>MARKUP</Text>
                                <Text style={[th, { width: 96, textAlign: 'right' }]}>AMOUNT</Text>
                                <Text style={[th, { width: 76 }]}>SUMMARY</Text>
                            </View>
                            <ScrollView style={{ maxHeight: 400 }}>
                                {weeks.map((g) => {
                                    if (budget <= 0) return null;
                                    const rows = g.rows.slice(0, budget);
                                    budget -= rows.length;
                                    const weekOn = g.rows.every((t) => sel[t.key]);
                                    const weekSome = !weekOn && g.rows.some((t) => sel[t.key]);
                                    const allSigned = g.days.size > 0 && g.signedDays.size === g.days.size;
                                    return (
                                        <View key={g.we || 'nodate'}>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#f1f5f9', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                                                <View style={[td, { width: 34 }]}>
                                                    <CheckBox checked={weekOn} indeterminate={weekSome} onPress={() => toggleMany(g.rows, !weekOn)} />
                                                </View>
                                                <Text style={[td, { fontWeight: '700', color: '#0f172a' }]}>
                                                    {g.we ? `Week ending ${fDate(g.we)}` : 'No work date'}
                                                </Text>
                                                <Text style={[td, { color: '#64748b', flexGrow: 1 }]}>
                                                    {`${g.rows.length} line${g.rows.length === 1 ? '' : 's'}`}
                                                    {g.hrs ? ` · ${(Math.round(g.hrs * 10) / 10).toLocaleString('en-US')} hrs` : ''}
                                                    {g.days.size && !sigLoading
                                                        ? ` · ${allSigned ? 'every work day' : `${g.signedDays.size} of ${g.days.size} work days`} under a signed summary`
                                                        : ''}
                                                </Text>
                                                <Text style={[td, { width: 96, textAlign: 'right', fontWeight: '700' }]}>{money(g.bill)}</Text>
                                                <View style={[td, { width: 76 }]}>
                                                    {allSigned ? <SigTag sig={{ stage: 'Signed', docs: [] }} /> : null}
                                                </View>
                                            </View>
                                            {rows.map((t, i) => (
                                                <Pressable
                                                    key={t.key}
                                                    onPress={() => toggleOne(t)}
                                                    style={{
                                                        flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9',
                                                        backgroundColor: sel[t.key] ? '#eff6ff' : i % 2 ? '#fafafa' : '#fff',
                                                    }}
                                                >
                                                    <View style={[td, { width: 34 }]}>
                                                        <CheckBox
                                                            checked={!!sel[t.key]}
                                                            onPress={(e) => {
                                                                if (e && e.stopPropagation) e.stopPropagation();
                                                                toggleOne(t);
                                                            }}
                                                        />
                                                    </View>
                                                    <Text style={[td, { width: 72 }]}>{fDate(t.date)}</Text>
                                                    <Text style={[td, { width: 92 }]} numberOfLines={1}>{t.cc}</Text>
                                                    <Text style={[td, { width: 96, color: '#475569' }]} numberOfLines={1}>{ctLabel(t.ct, t.ctDesc)}</Text>
                                                    <Text style={[td, { width: 160 }]} numberOfLines={1}>{t.who || '—'}</Text>
                                                    <Text style={[td, { flexGrow: 1, flexBasis: 0 }]} numberOfLines={1}>{t.eq || t.ds || '—'}</Text>
                                                    <Text style={[td, { width: 56, textAlign: 'right', color: '#64748b' }]}>
                                                        {t.hrs ? (Math.round(t.hrs * 10) / 10).toLocaleString('en-US') : ''}
                                                    </Text>
                                                    <Text style={[td, { width: 90, textAlign: 'right' }]}>{money(t.cost)}</Text>
                                                    <Text style={[td, { width: 84, textAlign: 'right' }]} numberOfLines={1}>{markupText(t)}</Text>
                                                    <Text style={[td, { width: 96, textAlign: 'right', fontWeight: '600' }]}>{money(t.bill)}</Text>
                                                    <View style={[td, { width: 76 }]}>
                                                        {ticketed(t) ? (sigLoading ? <Text style={{ fontSize: 11, color: '#cbd5e1' }}>…</Text> : <SigTag sig={sigOf(t)} />) : null}
                                                    </View>
                                                </Pressable>
                                            ))}
                                        </View>
                                    );
                                })}
                            </ScrollView>
                            {choices.length > ROW_CAP ? (
                                <Text style={{ fontSize: 11, color: '#64748b', paddingTop: 6 }}>
                                    Showing the newest {ROW_CAP} of {choices.length} — narrow with a section, cost type, search or the date range. Select-all and the week checkboxes still tick every match.
                                </Text>
                            ) : null}
                        </>
                    ) : (
                        <Text style={{ fontSize: 12, color: '#64748b' }}>
                            {lines.length
                                ? 'No unbilled transactions match these filters.'
                                : 'Every billable transaction is already on an invoice — nothing to add.'}
                        </Text>
                    )}
                </View>

                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, paddingBottom: 14, paddingLeft: 22, paddingRight: 22, borderTopWidth: 1, borderColor: '#e2e8f0' }}>
                    <Text style={{ fontSize: 12, color: '#64748b' }}>
                        {addList.length
                            ? `${addList.length} selected · ${money(addList.reduce((s, t) => s + t.bill, 0))} · stamped in the invoice's history`
                            : 'Select lines to add'}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <OutlineButton label="Cancel" onPress={onClose} />
                        <View style={{ width: 10 }} />
                        <Pressable
                            onPress={() => onAdd(inv, addList)}
                            style={{ paddingTop: 5, paddingBottom: 5, paddingLeft: 14, paddingRight: 14, borderRadius: 4, backgroundColor: '#0f172a', marginBottom: 6, opacity: addList.length ? 1 : 0.4 }}
                        >
                            <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>Add to {inv.no}</Text>
                        </Pressable>
                    </View>
                </View>
            </Pressable>
        </Pressable>
    );
}
